<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLocalStorage } from '@vueuse/core';
import { useAuthStore } from '../../stores/auth';
import {
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  MegaphoneIcon,
  MapPinIcon,
  ChartBarIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowLeftOnRectangleIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  QueueListIcon,
  CreditCardIcon,
  DocumentTextIcon
} from '@heroicons/vue/24/outline';
import { usePermissions } from '../../composables/usePermissions';
import UsageSidebar from '../billing/UsageSidebar.vue';
import UsageBanner from '../billing/UsageBanner.vue';
import LimitErrorBanner from '../billing/LimitErrorBanner.vue';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { canAccessPlanner } = usePermissions();
const sidebarOpen = ref(false);
const sidebarCollapsed = useLocalStorage('sidebarCollapsed', false);
const userMenuOpen = ref(false);

// Tooltip state
const activeTooltip = ref(null);
const tooltipPosition = ref({ top: 0, left: 0 });

function showTooltip(item, event) {
  if (!sidebarCollapsed.value) return;
  const rect = event.currentTarget.getBoundingClientRect();
  tooltipPosition.value = {
    top: rect.top + rect.height / 2,
    left: rect.right + 12
  };
  activeTooltip.value = item;
}

function hideTooltip() {
  activeTooltip.value = null;
}

// Set up click outside handler on mount
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

function handleClickOutside() {
  userMenuOpen.value = false;
}

const baseNavigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon, tooltip: 'View dashboard overview' },
  { name: 'Planner', href: '/planner', icon: ClipboardDocumentListIcon, tooltip: 'Plan and manage tentative events', requiresManager: true, requiresBeta: true, beta: true },
  { name: 'Locations', href: '/locations', icon: MapPinIcon, tooltip: 'Manage event locations' },
  { name: 'Campaigns', href: '/campaigns', icon: MegaphoneIcon, tooltip: 'Manage marketing campaigns' },
  { name: 'Events', href: '/events', icon: CalendarIcon, tooltip: 'Manage events and schedules' },
  { name: 'Attendees', href: '/attendees', icon: UserGroupIcon, tooltip: 'View and manage attendees' },
  { name: 'Leads', href: '/leads', icon: DocumentTextIcon, tooltip: 'Manage lead lists and outbound marketing' },
  { name: 'Attendee Queue', href: '/attendee-queue', icon: QueueListIcon, tooltip: 'Manage waitlisted attendees' },
  { name: 'Communication', href: '/communication', icon: EnvelopeIcon, tooltip: 'Send event reminders' },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon, tooltip: 'View analytics and reports' }
];

const navigation = computed(() => 
  baseNavigation.filter(item => {
    if (item.requiresManager && !canAccessPlanner.value) return false;
    if (item.requiresBeta && !authStore.betaEnabled) return false;
    return true;
  })
);

const currentRoute = computed(() => route.path);

function isCurrentRoute(href) {
  return currentRoute.value === href;
}

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value;
}

async function handleLogout() {
  try {
    userMenuOpen.value = false;
    await authStore.logout();
    router.push('/login');
  } catch (error) {
    console.log('Logout error:', JSON.stringify(error));
  }
}

function navigateToConfig() {
  userMenuOpen.value = false;
  router.push('/configuration');
}

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  // useLocalStorage automatically persists changes to localStorage
}
</script>

<template>
  <div>
    <!-- Mobile sidebar -->
    <div
      v-if="sidebarOpen"
      class="relative z-50 lg:hidden"
      role="dialog"
      aria-modal="true"
    >
      <div class="fixed inset-0 bg-gray-900/80"></div>

      <div class="fixed inset-0 flex">
        <div class="relative mr-16 flex w-full max-w-xs flex-1">
          <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
            <button
              type="button"
              class="-m-2.5 p-2.5"
              @click="sidebarOpen = false"
            >
              <span class="sr-only">Close sidebar</span>
              <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
            </button>
          </div>

          <div class="flex grow flex-col gap-y-3 overflow-y-auto bg-gradient-to-b from-gray-900 to-gray-800 px-4 pb-4">
            <div class="flex h-14 shrink-0 items-center">
              <div class="flex items-center gap-2">
                <img src="../../assets/eventflow-mark.png" alt="" class="h-8 w-auto" />
                <span class="text-white text-base font-semibold tracking-wide">EventFlow</span>
              </div>
            </div>
            <nav class="flex flex-1 flex-col">
              <ul role="list" class="flex flex-1 flex-col gap-y-4">
                <li>
                  <ul role="list" class="-mx-1 space-y-0.5">
                    <li v-for="item in navigation" :key="item.name">
                      <a
                        :href="item.href"
                        :title="item.tooltip"
                        :class="[
                          isCurrentRoute(item.href)
                            ? 'bg-gray-800 text-white'
                            : 'text-gray-300 hover:text-white hover:bg-gray-800',
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                        ]"
                      >
                        <component
                          :is="item.icon"
                          :class="[
                            isCurrentRoute(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-white',
                            'h-6 w-6 shrink-0'
                          ]"
                          aria-hidden="true"
                        />
                        {{ item.name }}
                        <span v-if="item.beta" class="ml-1 inline-flex items-center rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">β</span>
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div 
      :class="[
        'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col',
        sidebarCollapsed ? 'lg:w-16' : 'lg:w-56'
      ]"
    >
      <div class="flex grow flex-col gap-y-3 overflow-y-auto bg-gradient-to-b from-gray-900 to-gray-800 px-3 pb-4">
        <div class="flex h-14 shrink-0 items-center gap-2">
          <button
            type="button"
            class="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 ring-1 ring-white/15 hover:bg-white/20 transition-colors duration-200 cursor-pointer"
            @click="toggleSidebar"
            :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          >
            <Bars3Icon class="h-5 w-5 text-white" aria-hidden="true" />
          </button>
          <span :class="[sidebarCollapsed ? 'hidden' : 'text-white text-base font-semibold tracking-wide']">EventFlow</span>
          <img src="../../assets/eventflow-mark.png" alt="" :class="[sidebarCollapsed ? 'hidden' : 'h-5 w-auto']" />
        </div>
        <nav class="flex flex-1 flex-col">
          <ul role="list" class="flex flex-1 flex-col gap-y-4">
            <li>
              <ul role="list" class="-mx-1 space-y-0.5">
                <li v-for="item in navigation" :key="item.name" class="nav-item">
                  <a
                    :href="item.href"
                    :class="[
                      isCurrentRoute(item.href)
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:text-white hover:bg-gray-800',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors duration-200',
                      sidebarCollapsed ? 'justify-center' : ''
                    ]"
                    @mouseenter="showTooltip(item, $event)"
                    @mouseleave="hideTooltip"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        isCurrentRoute(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-white',
                        'h-6 w-6 shrink-0'
                      ]"
                      aria-hidden="true"
                    />
                    <span :class="[sidebarCollapsed ? 'hidden' : '']">{{ item.name }}</span>
                    <span v-if="item.beta && !sidebarCollapsed" class="ml-1 inline-flex items-center rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">β</span>
                  </a>
                </li>
              </ul>
            </li>
            <!-- Usage sidebar (only when expanded) -->
            <li v-if="!sidebarCollapsed" class="mt-auto border-t border-gray-700/50 pt-2">
              <UsageSidebar />
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Main content -->
    <div :class="[sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-56']">
      <div class="header-bar sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-8">
        <button
          type="button"
          class="-m-2.5 p-2.5 text-primary-200 hover:text-white transition-colors lg:hidden"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>

        <!-- Separator -->
        <div class="h-6 w-px bg-action-500/30 lg:hidden" aria-hidden="true" />

        <!-- Organization Name -->
        <div v-if="authStore.organizationName" class="hidden sm:flex items-center">
          <span class="text-sm font-medium text-primary-100">{{ authStore.organizationName }}</span>
        </div>

        <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
          <div class="flex flex-1"></div>
          <div class="flex items-center gap-x-4 lg:gap-x-6">
            <!-- Profile dropdown -->
            <div class="relative">
              <button
                type="button"
                class="user-menu-btn flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200"
                id="user-menu-button"
                :aria-expanded="userMenuOpen"
                aria-haspopup="true"
                @click.stop="toggleUserMenu"
              >
                <div class="user-avatar flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white">
                  {{ (authStore.user?.email || 'A')[0].toUpperCase() }}
                </div>
                <span class="hidden lg:flex lg:items-center gap-2">
                  <span class="text-sm font-medium text-primary-50">
                    {{ authStore.user?.email || 'Admin User' }}
                  </span>
                  <ChevronDownIcon 
                    :class="['h-4 w-4 text-primary-300 transition-transform duration-200', userMenuOpen ? 'rotate-180' : '']" 
                  />
                </span>
              </button>
              
              <!-- Dropdown menu -->
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <div
                  v-if="userMenuOpen"
                  id="user-menu-dropdown"
                  class="dropdown-menu absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl py-2 shadow-2xl ring-1 ring-white/10 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu-button"
                  @click.stop
                >
                  <div class="px-4 py-3 border-b border-white/10">
                    <p class="text-xs text-primary-300/70 uppercase tracking-wider">Signed in as</p>
                    <p class="text-sm font-medium text-white truncate mt-1">{{ authStore.user?.email || 'Admin User' }}</p>
                  </div>
                  <a
                    href="#"
                    class="dropdown-item group flex items-center gap-3 px-4 py-2.5 text-sm text-primary-100 hover:text-white transition-colors"
                    role="menuitem"
                    @click.prevent="navigateToConfig"
                  >
                    <CogIcon class="h-5 w-5 text-action-400 group-hover:text-action-300 transition-colors" />
                    Configuration
                  </a>
                  <a
                    href="/billing"
                    class="dropdown-item group flex items-center gap-3 px-4 py-2.5 text-sm text-primary-100 hover:text-white transition-colors"
                    role="menuitem"
                  >
                    <CreditCardIcon class="h-5 w-5 text-action-400 group-hover:text-action-300 transition-colors" />
                    Billing
                  </a>
                  <a
                    href="#"
                    class="dropdown-item group flex items-center gap-3 px-4 py-2.5 text-sm text-primary-100 hover:text-white transition-colors"
                    role="menuitem"
                    @click.prevent="handleLogout"
                  >
                    <ArrowLeftOnRectangleIcon class="h-5 w-5 text-action-400 group-hover:text-danger-400 transition-colors" />
                    Sign out
                  </a>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>

      <UsageBanner />
      <main class="py-10">
        <div class="px-4 sm:px-6 lg:px-8">
          <slot></slot>
        </div>
      </main>
    </div>

    <LimitErrorBanner />

    <!-- Teleported tooltip for collapsed sidebar -->
    <Teleport to="body">
      <Transition name="tooltip">
        <div 
          v-if="activeTooltip"
          class="sidebar-tooltip"
          :style="{
            top: tooltipPosition.top + 'px',
            left: tooltipPosition.left + 'px'
          }"
        >
          <div class="sidebar-tooltip-arrow"></div>
          <span class="font-semibold">{{ activeTooltip.name }}</span>
          <span class="block text-xs text-primary-300/70 mt-0.5">{{ activeTooltip.tooltip }}</span>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
@use '../../assets/scss/variables' as *;

/* Header bar — brand primary navy gradient */
.header-bar {
  background: linear-gradient(135deg, $primary-950 0%, $primary-800 50%, $primary-900 100%);
  border-bottom: 1px solid rgba($action, 0.15);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.3),
    0 0 40px rgba($action, 0.05) inset;
  position: relative;
}

.header-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba($action, 0.35), transparent);
  pointer-events: none;
}

.header-bar::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  background: radial-gradient(ellipse at top right, rgba($action, 0.08) 0%, transparent 70%);
  pointer-events: none;
}

/* User menu button */
.user-menu-btn {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);

  &:hover {
    background: rgba($action, 0.1);
    border-color: rgba($action, 0.25);
  }
}

/* User avatar */
.user-avatar {
  background: linear-gradient(135deg, $action 0%, $action-700 100%);
  box-shadow: 0 2px 10px rgba($action, 0.4);
}

/* Dropdown menu */
.dropdown-menu {
  background: linear-gradient(180deg, $gray-800 0%, $primary-950 100%);
  border: 1px solid rgba($action, 0.15);
  backdrop-filter: blur(20px);
}

.dropdown-item {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, $action, $action-700);
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    background: rgba($action, 0.08);
  }
}

/* Sidebar navigation item */
.nav-item {
  position: relative;
}
</style>

<!-- Non-scoped styles for teleported tooltip -->
<style lang="scss">
@use '../../assets/scss/variables' as *;

/* Sidebar tooltip styles - fixed positioning via Teleport */
.sidebar-tooltip {
  position: fixed;
  transform: translateY(-50%);
  padding: 0.625rem 0.875rem;
  background: linear-gradient(135deg, $gray-800 0%, $primary-950 100%);
  color: white;
  font-size: 0.875rem;
  border-radius: 0.5rem;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba($action, 0.15),
    0 0 20px rgba($action, 0.1);
  white-space: nowrap;
  z-index: 9999;
  pointer-events: none;
}

.sidebar-tooltip-arrow {
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-right-color: $gray-800;
}

/* Tooltip transition */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(-8px);
}

.tooltip-enter-to,
.tooltip-leave-from {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}
</style>